import React, { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { FormHelperText } from '@material-ui/core';

import { AddBlackListSchema } from '@project/validators';
import { AddMunicipalityBlackListProps, BaseFormProps, ErrorObject } from '@project/types';
import { Button } from '@project/components';

export const AddMunicipalityBlackListForm: React.FC<BaseFormProps<AddMunicipalityBlackListProps>> =
  ({ initialValues, submitForm, onSuccess }) => {
    const _handleFormSubmitError = (
      error: ErrorObject,
      actions: FormikHelpers<AddMunicipalityBlackListProps>,
    ) => {
      actions.setSubmitting(false);
      actions.setStatus(error.detail);
    };

    const _handleSubmission = (
      formData: AddMunicipalityBlackListProps,
      actions: FormikHelpers<AddMunicipalityBlackListProps>,
    ) => {
      submitForm(formData)
        .then(() => {
          onSuccess();
        })
        .catch((error: ErrorObject) => {
          _handleFormSubmitError(error, actions);
        })
        .finally(() => actions.setSubmitting(false));
    };

    return (
      <Formik
        initialValues={initialValues}
        validateOnBlur
        validateOnChange={false}
        onSubmit={_handleSubmission}
        validationSchema={AddBlackListSchema}
      >
        {({
          handleSubmit,
          isSubmitting,
          values,
          touched,
          errors,
          handleReset,
          status,
          setFieldValue,
        }) => {
          const [newMunicipality, setNewMunicipality] = useState('');

          const addMunicipality = () => {
            if (newMunicipality.trim() !== '') {
              setFieldValue('municipalities', [...values.municipalities, newMunicipality.trim()]);
              setNewMunicipality('');
            }
          };

          const removeMunicipality = (index: number) => {
            const updated = values.municipalities.filter((_, i) => i !== index);
            setFieldValue('municipalities', updated);
          };

          return (
            <form
              onReset={handleReset}
              onSubmit={handleSubmit}
              className="flex flex-auto flex-col gap-3 py-1 mx-7"
            >
              <p className="flex justify-center auth-heading text-center">Municipalities</p>
              {status && <FormHelperText error>{status}</FormHelperText>}
              <p className="text-xs text-left">Municipality Name :</p>
              <div className="flex items-center gap-2 self-center w-96">
                <input
                  type="text"
                  value={newMunicipality}
                  onChange={(e) => setNewMunicipality(e.target.value)}
                  className="flex-grow border p-2 rounded"
                  placeholder="Enter municipality name"
                />
                <Button
                  onClick={addMunicipality}
                  className="bg-blue-600 text-white px-3 py-2 rounded"
                >
                  Add
                </Button>
              </div>
              {touched.municipalities && typeof errors.municipalities === 'string' && (
                <FormHelperText error>{errors.municipalities}</FormHelperText>
              )}

              {values.municipalities.length > 0 && (
                <ul className="list-disc pl-5 self-center w-96">
                  {values.municipalities.map((municipality, index) => (
                    <li key={municipality} className="flex justify-between items-center my-1">
                      <span>{municipality}</span>
                      <Button
                        onClick={() => removeMunicipality(index)}
                        className="bg-red-500 text-white px-2 py-1 rounded text-xs"
                      >
                        Remove
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              <Button
                isLoading={isSubmitting}
                onClick={handleSubmit}
                className="w-4/5 bg-secondary hover:bg-secondary-dark text-white text-base self-center my-7"
              >
                Add Municipality List
              </Button>
            </form>
          );
        }}
      </Formik>
    );
  };
